<template>
  <div
    class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3.5 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="hidden md:block h-64 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- =====================================================
             SECTION 1: LIVE EDITORIAL PREVIEWS (DESKTOP & TABLET ONLY)
        ===================================================== -->
        <section class="hidden md:block space-y-4">
          <div class="flex items-center justify-between px-1">
            <div class="flex items-center gap-2.5">
              <span class="relative flex h-2.5 w-2.5">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"
                ></span>
              </span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5"
              >
                <EyeIcon class="h-4 w-4 text-indigo-600" />
                Live Front-End Simulation
              </h3>
            </div>

            <div class="flex items-center gap-2">
              <span
                class="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700"
              >
                Realtime Mirror
              </span>
            </div>
          </div>

          <!-- 1. Principal Desk Dark Banner Live Preview -->
          <div
            class="rounded-3xl border border-slate-900 bg-[#11151F] p-6 sm:p-10 text-white shadow-md relative overflow-hidden"
          >
            <div
              class="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center relative z-10"
            >
              <!-- Avatar Ring -->
              <div class="md:col-span-4 flex justify-center">
                <div class="relative">
                  <div
                    class="w-36 h-36 sm:w-44 sm:h-44 rounded-full p-2 border border-[#B8863B] shrink-0 bg-slate-900 overflow-hidden"
                  >
                    <img
                      v-if="principalPhotoPreview || formData.principalPhoto"
                      :src="principalPhotoPreview || formData.principalPhoto"
                      alt="Principal Photo"
                      class="w-full h-full object-cover rounded-full border border-[#B8863B]/40"
                    />
                    <div
                      v-else
                      class="w-full h-full rounded-full bg-slate-800 flex flex-col items-center justify-center text-slate-400"
                    >
                      <UserIcon class="h-10 w-10 text-slate-500 mb-1" />
                      <span
                        class="text-[9px] font-mono uppercase font-bold text-slate-400"
                        >No Photo</span
                      >
                    </div>
                  </div>
                  <div
                    class="absolute bottom-1 right-1 w-10 h-10 rounded-full bg-[#B8863B] text-[#11151F] flex items-center justify-center text-sm font-bold border-2 border-[#11151F] shadow-sm"
                  >
                    <QuoteIcon class="h-4 w-4" />
                  </div>
                </div>
              </div>

              <!-- Content Copy -->
              <div class="md:col-span-8 space-y-3 text-center md:text-left">
                <div class="inline-flex items-center gap-2">
                  <span class="w-4 h-[1.5px] bg-[#B8863B]"></span>
                  <span
                    class="font-mono text-[10px] font-bold tracking-widest text-[#D8B372] uppercase"
                  >
                    {{
                      formData.principalEyebrow || "From the Principal's Desk"
                    }}
                  </span>
                  <span class="w-4 h-[1.5px] bg-[#B8863B]"></span>
                </div>

                <p
                  class="font-serif italic text-base sm:text-xl text-slate-100 leading-relaxed max-w-2xl"
                >
                  "{{
                    formData.principalMessage ||
                    "Every child who walks through our gates carries a spark worth protecting..."
                  }}"
                </p>

                <div class="pt-3 border-t border-[#B8863B]/30">
                  <h4
                    class="font-serif italic text-lg sm:text-xl text-[#B8863B] font-medium"
                  >
                    {{ formData.principalName || "Dr. Principal Name" }}
                  </h4>
                  <p
                    class="font-mono text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mt-0.5"
                  >
                    {{ formData.principalDesignation || "Principal" }}
                    <span v-if="formData.principalQualification"
                      >&middot; {{ formData.principalQualification }}</span
                    >
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- 2. Board & Executive Officers Live Preview Strip -->
          <div
            v-if="formData.executiveOfficers.length > 0"
            class="rounded-3xl border border-[#E1D8C6] bg-[#FAF7F1] p-6 sm:p-8 space-y-4 shadow-xs"
          >
            <div class="flex items-center gap-2">
              <span class="w-3 h-0.5 bg-[#B8863B]"></span>
              <h4
                class="font-mono text-[11px] font-bold uppercase tracking-wider text-[#1F3A5F]"
              >
                Board & Executive Officers Preview
              </h4>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="(officer, oIdx) in formData.executiveOfficers"
                :key="officer._id || oIdx"
                class="rounded-2xl border border-[#E1D8C6] bg-white p-5 flex flex-col justify-between shadow-xs transition hover:border-[#B8863B] min-h-[140px]"
              >
                <div class="flex items-center gap-3.5 mb-3 min-w-0">
                  <!-- Fixed & Constrained Avatar Frame -->
                  <div
                    class="w-14 h-14 min-w-[3.5rem] min-h-[3.5rem] rounded-full border-2 border-[#B8863B] overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center relative"
                  >
                    <img
                      v-if="officer.previewUrl || officer.photo"
                      :src="officer.previewUrl || officer.photo"
                      :alt="officer.name"
                      class="w-full h-full object-cover rounded-full aspect-square"
                    />
                    <UserIcon
                      v-else
                      class="h-6 w-6 text-slate-400"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <span
                      class="font-mono text-[9.5px] font-bold uppercase text-[#B8863B] block tracking-wider truncate"
                    >
                      {{ officer.eyebrow || "Executive Desk" }}
                    </span>
                    <h5
                      class="font-bold text-sm sm:text-base text-[#11151F] truncate"
                    >
                      {{ officer.name || "Officer Name" }}
                    </h5>
                    <span
                      class="font-mono text-[10px] font-bold text-[#1F3A5F] uppercase truncate block"
                    >
                      {{ officer.designation || "Designation" }}
                    </span>
                  </div>
                </div>

                <p
                  v-if="officer.message"
                  class="font-serif italic text-xs sm:text-[13px] text-[#4B5262] leading-relaxed line-clamp-3"
                >
                  "{{ officer.message }}"
                </p>
              </div>
            </div>
          </div>

          <!-- 3. Admission Process Live Preview Track -->
          <div
            v-if="formData.admissionSteps.length > 0"
            class="rounded-3xl border border-[#E1D8C6] bg-[#FAF7F1] p-6 sm:p-8 space-y-4 shadow-xs"
          >
            <div class="flex items-center gap-2">
              <span class="w-3 h-0.5 bg-[#B8863B]"></span>
              <h4
                class="font-mono text-[11px] font-bold uppercase tracking-wider text-[#1F3A5F]"
              >
                Admission Steps Flow Preview
              </h4>
            </div>

            <div
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4"
            >
              <div
                v-for="(step, sIdx) in formData.admissionSteps"
                :key="step._id || sIdx"
                class="rounded-2xl border border-[#E1D8C6] bg-white p-4 relative flex flex-col justify-between shadow-xs transition hover:-translate-y-1 hover:border-[#B8863B]"
              >
                <span
                  class="absolute top-3 right-3.5 font-serif font-black text-xl text-slate-200 select-none"
                >
                  0{{ sIdx + 1 }}
                </span>

                <div class="space-y-2.5">
                  <div
                    class="w-9 h-9 rounded-full bg-[#11151F] text-[#D8B372] flex items-center justify-center text-xs shadow-xs"
                  >
                    <i :class="['fas', step.icon || 'fa-circle-check']"></i>
                  </div>

                  <h5
                    class="font-bold text-xs sm:text-sm text-[#11151F] leading-snug pr-6"
                  >
                    {{ step.title || "Step Title" }}
                  </h5>

                  <p
                    class="text-[11px] text-[#4B5262] leading-relaxed line-clamp-3"
                  >
                    {{ step.description || "Step process details..." }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- =====================================================
             SECTION 2: EDITORIAL FORMS (RESPONSIVE FOR ALL SCREENS)
        ===================================================== -->
        <div
          class="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start"
        >
          <!-- LEFT COLUMN: LEADERSHIP DESKS -->
          <div class="lg:col-span-7 space-y-5 sm:space-y-6">
            <!-- Principal's Desk Settings -->
            <section
              class="rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4 sm:space-y-5"
            >
              <div
                class="flex items-center space-x-2.5 border-b border-slate-100 pb-3.5 sm:pb-4"
              >
                <div
                  class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"
                >
                  <GraduationCapIcon class="h-4 w-4" />
                </div>
                <div>
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight"
                  >
                    Principal's Desk
                  </h2>
                  <p class="text-[11px] text-slate-500">
                    Main leadership quote and credential details
                  </p>
                </div>
              </div>

              <div class="space-y-3.5 sm:space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Section Eyebrow</label
                    >
                    <input
                      v-model="formData.principalEyebrow"
                      type="text"
                      placeholder="From the Principal's Desk"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-medium text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Section Title</label
                    >
                    <input
                      v-model="formData.principalTitle"
                      type="text"
                      placeholder="Leadership & Vision"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-3.5">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Principal Name *</label
                    >
                    <input
                      v-model="formData.principalName"
                      type="text"
                      placeholder="Dr. Anjali Bordoloi"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Designation</label
                    >
                    <input
                      v-model="formData.principalDesignation"
                      type="text"
                      placeholder="Principal"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-medium text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Qualification</label
                    >
                    <input
                      v-model="formData.principalQualification"
                      type="text"
                      placeholder="M.Ed., Ph.D."
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-medium text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>
                </div>

                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                    >Principal's Vision Message</label
                  >
                  <textarea
                    v-model="formData.principalMessage"
                    rows="3"
                    placeholder="Write the principal's official message or statement..."
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2.5 text-xs text-slate-700 leading-relaxed focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  ></textarea>
                </div>

                <!-- Principal Photo Upload -->
                <div class="pt-2 border-t border-slate-100">
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2"
                    >Principal Portrait</label
                  >
                  <div class="flex items-center gap-3.5 sm:gap-4">
                    <div
                      class="relative group rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden bg-slate-50 h-20 w-20 sm:h-24 sm:w-24 flex flex-col items-center justify-center p-2 text-center shrink-0 hover:border-indigo-400 transition"
                    >
                      <img
                        v-if="principalPhotoPreview || formData.principalPhoto"
                        :src="principalPhotoPreview || formData.principalPhoto"
                        class="absolute inset-0 w-full h-full object-cover"
                      />
                      <div
                        v-else
                        class="space-y-1 text-slate-400"
                      >
                        <ImageIcon class="mx-auto h-4 w-4" />
                        <span
                          class="block text-[8.5px] font-bold uppercase tracking-wider"
                          >Upload</span
                        >
                      </div>

                      <input
                        type="file"
                        accept="image/*"
                        class="absolute inset-0 opacity-0 cursor-pointer z-10"
                        @change="handlePrincipalPhotoSelect"
                      />

                      <button
                        v-if="principalPhotoPreview || formData.principalPhoto"
                        type="button"
                        class="absolute top-1 right-1 z-20 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition cursor-pointer"
                        @click.stop="removePrincipalPhoto"
                      >
                        <XIcon class="h-2.5 w-2.5" />
                      </button>
                    </div>

                    <div class="text-xs text-slate-500 space-y-0.5">
                      <p class="font-bold text-slate-700 text-xs">
                        Official Headshot
                      </p>
                      <p class="text-[11px] text-slate-500 leading-relaxed">
                        Square (1:1) aspect ratio recommended.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Dynamic Board & Executive Officers Section -->
            <section
              class="rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4 sm:space-y-5"
            >
              <div
                class="flex items-center justify-between border-b border-slate-100 pb-3.5 sm:pb-4"
              >
                <div class="flex items-center space-x-2.5 min-w-0">
                  <div
                    class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"
                  >
                    <AwardIcon class="h-4 w-4" />
                  </div>
                  <div class="min-w-0">
                    <h2
                      class="text-sm sm:text-base font-bold text-slate-900 leading-tight truncate"
                    >
                      Executive Officers
                    </h2>
                    <p class="text-[11px] text-slate-500 truncate">
                      Chairman, Vice Principal, Trustees
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded-xl bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 active:scale-95 transition cursor-pointer shrink-0"
                  @click="addExecutiveOfficer"
                >
                  <PlusIcon class="h-3.5 w-3.5 text-amber-600" />
                  <span>Add Officer</span>
                </button>
              </div>

              <!-- Executive Officers Cards List -->
              <div class="space-y-3.5 sm:space-y-4">
                <div
                  v-for="(officer, index) in formData.executiveOfficers"
                  :key="officer._id || index"
                  class="rounded-2xl border border-slate-200/90 bg-slate-50/60 p-3.5 sm:p-5 space-y-3 sm:space-y-4 relative group"
                >
                  <div
                    class="flex items-center justify-between border-b border-slate-200/70 pb-2.5 sm:pb-3"
                  >
                    <span
                      class="font-mono text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 truncate"
                    >
                      <span
                        class="w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center text-[9.5px] shrink-0"
                      >
                        {{ index + 1 }}
                      </span>
                      {{ officer.designation || "Officer Profile" }}
                    </span>

                    <button
                      type="button"
                      class="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                      title="Delete Officer"
                      @click="removeExecutiveOfficer(index)"
                    >
                      <Trash2Icon class="h-4 w-4" />
                    </button>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                    <div>
                      <label
                        class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                        >Eyebrow</label
                      >
                      <input
                        v-model="officer.eyebrow"
                        type="text"
                        placeholder="From the Chairman's Desk"
                        class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                        >Officer Name *</label
                      >
                      <input
                        v-model="officer.name"
                        type="text"
                        placeholder="Shri Name"
                        class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                        >Designation</label
                      >
                      <input
                        v-model="officer.designation"
                        type="text"
                        placeholder="Chairman / Vice Principal"
                        class="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      class="block text-[10px] font-bold text-slate-600 uppercase mb-1"
                      >Statement / Message</label
                    >
                    <textarea
                      v-model="officer.message"
                      rows="2"
                      placeholder="Official statement or address..."
                      class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed focus:border-indigo-500 focus:outline-none"
                    ></textarea>
                  </div>

                  <!-- Officer Photo Upload -->
                  <div
                    class="pt-2 border-t border-slate-200/70 flex items-center gap-3.5 sm:gap-4"
                  >
                    <div
                      class="relative group/pic rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white h-14 w-14 sm:h-16 sm:w-16 flex flex-col items-center justify-center text-center shrink-0 hover:border-amber-400 transition"
                    >
                      <img
                        v-if="officer.previewUrl || officer.photo"
                        :src="officer.previewUrl || officer.photo"
                        class="absolute inset-0 w-full h-full object-cover rounded-xl"
                      />
                      <div
                        v-else
                        class="text-slate-400"
                      >
                        <ImageIcon class="mx-auto h-3.5 w-3.5" />
                        <span
                          class="block text-[8px] font-bold uppercase mt-0.5"
                          >Photo</span
                        >
                      </div>

                      <input
                        type="file"
                        accept="image/*"
                        class="absolute inset-0 opacity-0 cursor-pointer z-10"
                        @change="(e) => handleOfficerPhotoSelect(e, index)"
                      />

                      <button
                        v-if="officer.previewUrl || officer.photo"
                        type="button"
                        class="absolute top-1 right-1 z-20 p-0.5 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition cursor-pointer"
                        @click.stop="removeOfficerPhoto(index)"
                      >
                        <XIcon class="h-2.5 w-2.5" />
                      </button>
                    </div>

                    <div class="text-[11px] text-slate-500">
                      <span class="font-bold text-slate-700 block"
                        >Officer Portrait</span
                      >
                      <span>Displays beside the officer quote</span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="formData.executiveOfficers.length === 0"
                  class="p-6 sm:p-8 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 space-y-2"
                >
                  <AwardIcon class="mx-auto h-7 w-7 text-slate-300" />
                  <p class="text-xs text-slate-500 font-medium">
                    No Board or Executive Officers added.
                  </p>
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 border border-indigo-100 text-xs font-bold text-indigo-600 hover:bg-indigo-100 cursor-pointer"
                    @click="addExecutiveOfficer"
                  >
                    <PlusIcon class="h-3.5 w-3.5" />
                    <span>Add Chairman / Vice Principal</span>
                  </button>
                </div>
              </div>
            </section>
          </div>

          <!-- RIGHT COLUMN: ADMISSIONS PROCESS STEP BUILDER -->
          <section
            class="lg:col-span-5 rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3.5 sm:pb-4"
            >
              <div class="flex items-center space-x-2.5 min-w-0">
                <div
                  class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"
                >
                  <ListOrderedIcon class="h-4 w-4" />
                </div>
                <div class="min-w-0">
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight truncate"
                  >
                    Admission Steps
                  </h2>
                  <p class="text-[11px] text-slate-500 truncate">
                    Sequential registration workflow
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-xl bg-indigo-50 border border-indigo-100 px-2.5 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100 active:scale-95 transition cursor-pointer shrink-0"
                @click="addAdmissionStep"
              >
                <PlusIcon class="h-3.5 w-3.5 text-indigo-600" />
                <span>Add Step</span>
              </button>
            </div>

            <!-- Steps List with Live Drag/Order Inputs -->
            <div class="space-y-3">
              <div
                v-for="(step, index) in formData.admissionSteps"
                :key="step._id || index"
                class="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-3 sm:p-3.5 space-y-2 relative"
              >
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      class="w-5 h-5 rounded-full bg-slate-900 text-white font-mono text-[10px] font-bold flex items-center justify-center shrink-0"
                    >
                      {{ index + 1 }}
                    </span>
                    <input
                      v-model="step.title"
                      type="text"
                      placeholder="Step Title (e.g. Submit Enquiry)"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>

                  <button
                    type="button"
                    class="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer shrink-0"
                    @click="removeAdmissionStep(index)"
                  >
                    <Trash2Icon class="h-4 w-4" />
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div class="sm:col-span-1">
                    <label
                      class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5"
                      >Icon Class</label
                    >
                    <input
                      v-model="step.icon"
                      type="text"
                      placeholder="fa-file-pen"
                      class="w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-mono text-slate-700 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div class="sm:col-span-2">
                    <label
                      class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5"
                      >Description</label
                    >
                    <textarea
                      v-model="step.description"
                      rows="2"
                      placeholder="Explain this registration stage..."
                      class="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none"
                    ></textarea>
                  </div>
                </div>
              </div>

              <div
                v-if="formData.admissionSteps.length === 0"
                class="p-6 text-center border-2 border-dashed border-slate-200/80 rounded-2xl bg-slate-50/50"
              >
                <p class="text-xs text-slate-400">
                  No admission stages configured.
                </p>
                <button
                  type="button"
                  class="mt-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                  @click="addAdmissionStep"
                >
                  + Add First Step
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- =====================================================
           COMPACT STICKY BOTTOM ACTION BAR
      ===================================================== -->
      <!-- COMPACT STICKY BOTTOM ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-md pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 sm:px-2.5 sm:py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0 text-[11px] sm:text-xs"
              >
                <span
                  class="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate text-[11px] sm:text-xs"
              >
                <CheckCircleIcon class="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span class="truncate">All changes saved</span>
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-3.5 sm:px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Changes"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all leadership information and
          admission process steps back to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    Image as ImageIcon,
    ListOrdered as ListOrderedIcon,
    X as XIcon,
    Quote as QuoteIcon,
    Eye as EyeIcon,
    User as UserIcon,
    Award as AwardIcon,
    GraduationCap as GraduationCapIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);

  const principalPhotoFile = ref(null);
  const principalPhotoPreview = ref("");
  const initialStateJson = ref("");

  const formData = reactive({
    // Principal Profile
    principalEyebrow: "From the Principal's Desk",
    principalTitle: "Leadership & Vision",
    principalName: "",
    principalDesignation: "Principal",
    principalQualification: "",
    principalMessage: "",
    principalPhoto: "",

    // Dynamic Array for Board & Executive Officers (Chairman, Vice Principal, Trustees)
    executiveOfficers: [],

    // Admission Stages
    admissionSteps: [],
  });

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  function getCleanState() {
    return {
      principalEyebrow: formData.principalEyebrow || "",
      principalTitle: formData.principalTitle || "",
      principalName: formData.principalName || "",
      principalDesignation: formData.principalDesignation || "",
      principalQualification: formData.principalQualification || "",
      principalMessage: formData.principalMessage || "",
      principalPhoto:
        typeof formData.principalPhoto === "string"
          ? formData.principalPhoto
          : "",
      hasNewPrincipalPhoto: principalPhotoFile.value instanceof File,

      executiveOfficers: formData.executiveOfficers.map((o) => ({
        _id: o._id,
        eyebrow: o.eyebrow || "",
        name: o.name || "",
        designation: o.designation || "",
        message: o.message || "",
        photo: typeof o.photo === "string" ? o.photo : "",
        hasNewFile: o.file instanceof File,
      })),

      admissionSteps: formData.admissionSteps.map((s) => ({
        _id: s._id,
        icon: s.icon || "",
        title: s.title || "",
        description: s.description || "",
        sortOrder: s.sortOrder,
      })),
    };
  }

  const hasUnsavedChanges = computed(() => {
    return JSON.stringify(getCleanState()) !== initialStateJson.value;
  });

  function snapshotState() {
    initialStateJson.value = JSON.stringify(getCleanState());
  }

  function handlePrincipalPhotoSelect(event) {
    const file = event.target.files[0];
    if (!file) return;
    principalPhotoFile.value = file;
    principalPhotoPreview.value = URL.createObjectURL(file);
  }

  function removePrincipalPhoto() {
    principalPhotoFile.value = null;
    principalPhotoPreview.value = "";
    formData.principalPhoto = "";
  }

  /* Executive Officer Methods */
  function addExecutiveOfficer() {
    formData.executiveOfficers.push({
      eyebrow: "From the Executive Desk",
      name: "",
      designation: "Executive Officer",
      message: "",
      photo: "",
      file: null,
      previewUrl: "",
    });
  }

  function removeExecutiveOfficer(index) {
    formData.executiveOfficers.splice(index, 1);
  }

  function handleOfficerPhotoSelect(event, index) {
    const file = event.target.files[0];
    if (!file) return;
    formData.executiveOfficers[index].file = file;
    formData.executiveOfficers[index].previewUrl = URL.createObjectURL(file);
  }

  function removeOfficerPhoto(index) {
    formData.executiveOfficers[index].file = null;
    formData.executiveOfficers[index].previewUrl = "";
    formData.executiveOfficers[index].photo = "";
  }

  /* Admission Step Methods */
  function addAdmissionStep() {
    formData.admissionSteps.push({
      icon: "fa-file-pen",
      title: "New Admission Step",
      description: "",
      sortOrder: formData.admissionSteps.length,
    });
  }

  function removeAdmissionStep(index) {
    formData.admissionSteps.splice(index, 1);
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details =
        store.state.website?.details || store.state.websiteDetails || {};

      formData.principalEyebrow =
        details.principalEyebrow || "From the Principal's Desk";
      formData.principalTitle = details.principalTitle || "Leadership & Vision";
      formData.principalName = details.principalName || "";
      formData.principalDesignation =
        details.principalDesignation || "Principal";
      formData.principalQualification = details.principalQualification || "";
      formData.principalMessage = details.principalMessage || "";
      formData.principalPhoto = details.principalPhoto || "";

      // Parse executive officers with root field fallback reconstruction
      if (
        Array.isArray(details.executiveOfficers) &&
        details.executiveOfficers.length > 0
      ) {
        formData.executiveOfficers = details.executiveOfficers.map((o) => ({
          _id: o._id,
          eyebrow: o.eyebrow || "From the Desk",
          name: o.name || "",
          designation: o.designation || "",
          message: o.message || "",
          photo: o.photo || "",
          file: null,
          previewUrl: "",
        }));
      } else {
        const initialOfficers = [];
        if (details.chairmanName || details.chairmanMessage) {
          initialOfficers.push({
            eyebrow: details.chairmanEyebrow || "From the Chairman's Desk",
            name: details.chairmanName || "",
            designation: details.chairmanDesignation || "Chairman",
            message: details.chairmanMessage || "",
            photo: details.chairmanPhoto || "",
            file: null,
            previewUrl: "",
          });
        }
        if (details.vicePrincipalName || details.vicePrincipalMessage) {
          initialOfficers.push({
            eyebrow:
              details.vicePrincipalEyebrow || "From the Vice Principal's Desk",
            name: details.vicePrincipalName || "",
            designation: details.vicePrincipalDesignation || "Vice Principal",
            message: details.vicePrincipalMessage || "",
            photo: details.vicePrincipalPhoto || "",
            file: null,
            previewUrl: "",
          });
        }
        formData.executiveOfficers = initialOfficers;
      }

      if (
        Array.isArray(details.admissionSteps) &&
        details.admissionSteps.length > 0
      ) {
        formData.admissionSteps = details.admissionSteps.map((st, idx) => ({
          _id: st._id,
          icon: st.icon || "fa-file-pen",
          title: st.title || "",
          description: st.description || "",
          sortOrder: st.sortOrder !== undefined ? st.sortOrder : idx,
        }));
      } else {
        formData.admissionSteps = [
          {
            icon: "fa-file-pen",
            title: "Submit Enquiry",
            description: "Fill out the online enquiry form or visit campus.",
            sortOrder: 0,
          },
          {
            icon: "fa-comments",
            title: "Campus Interaction",
            description: "Interaction with our faculty mentors.",
            sortOrder: 1,
          },
          {
            icon: "fa-file-circle-check",
            title: "Document Verification",
            description: "Submit prior academic records and proof.",
            sortOrder: 2,
          },
          {
            icon: "fa-hand-holding-dollar",
            title: "Confirm Admission",
            description: "Complete fee clearance to secure seat.",
            sortOrder: 3,
          },
        ];
      }

      principalPhotoFile.value = null;
      principalPhotoPreview.value = "";
      snapshotState();
    } catch (err) {
      showToast("Failed to load leadership data", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const chairman = formData.executiveOfficers[0] || {};
      const vicePrincipal = formData.executiveOfficers[1] || {};

      const payload = {
        principalEyebrow: formData.principalEyebrow,
        principalTitle: formData.principalTitle,
        principalName: formData.principalName,
        principalDesignation: formData.principalDesignation,
        principalQualification: formData.principalQualification,
        principalMessage: formData.principalMessage,
        principalPhoto:
          principalPhotoFile.value || formData.principalPhoto || "",

        // Backward compatibility root sync
        chairmanEyebrow: chairman.eyebrow || "",
        chairmanName: chairman.name || "",
        chairmanDesignation: chairman.designation || "Chairman",
        chairmanMessage: chairman.message || "",
        chairmanPhoto: chairman.file || chairman.photo || "",

        vicePrincipalEyebrow: vicePrincipal.eyebrow || "",
        vicePrincipalName: vicePrincipal.name || "",
        vicePrincipalDesignation: vicePrincipal.designation || "Vice Principal",
        vicePrincipalMessage: vicePrincipal.message || "",
        vicePrincipalPhoto: vicePrincipal.file || vicePrincipal.photo || "",

        // Full dynamic array
        executiveOfficers: formData.executiveOfficers.map((o) => ({
          _id: o._id,
          eyebrow: o.eyebrow || "",
          name: o.name || "",
          designation: o.designation || "",
          message: o.message || "",
          photo: o.file || o.photo || "",
        })),

        admissionSteps: formData.admissionSteps.map((st, idx) => ({
          _id: st._id,
          icon: st.icon || "fa-file-pen",
          title: st.title || "",
          description: st.description || "",
          sortOrder: st.sortOrder !== undefined ? st.sortOrder : idx,
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast(
        "Leadership & Admissions details saved successfully!",
        "success",
      );
    } catch (err) {
      showToast(err.message || "Error saving details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
